import React from 'react';
import { Link } from 'react-router-dom';
import { APP_TRANSLATIONS } from '../constants/translations';
import { trackSocialClick } from '../analytics';
import { isConsentRegion, openConsentSettings } from '../consent';
import { aboutHref, blogHref, homeHref } from '../siteLocale';
import PreferredSourceLink from './PreferredSourceLink';
import { DiscordIcon, InstagramIcon, TikTokIcon, TwitchIcon, YouTubeIcon } from './SocialIcons';
import { SOCIAL_LINKS } from '../constants/socialLinks';

const BUY_ME_A_COFFEE_URL = 'https://buymeacoffee.com/ferbrambi';

const COFFEE_CTA_BY_LANG = {
  es: '¿Te ayudó esta base de datos? Invítame un café',
  en: 'Did this database help you? Buy me a coffee',
  pt: 'Esta base de dados te ajudou? Me pague um café'
};

const NEWS_TAGLINE_BY_LANG = {
  es: 'Noticias, guías y estrategias de Marvel Rivals en español, inglés y portugués.',
  en: 'Marvel Rivals news, guides, and strategies in English, Spanish, and Portuguese.',
  pt: 'Notícias, guias e estratégias de Marvel Rivals em português, inglês e espanhol.'
};

export default function SiteFooter({ lang = 'en' }) {
  const coffeeCta = COFFEE_CTA_BY_LANG[lang] || COFFEE_CTA_BY_LANG.en;
  const newsTagline = NEWS_TAGLINE_BY_LANG[lang] || NEWS_TAGLINE_BY_LANG.en;
  const t = APP_TRANSLATIONS[lang] || APP_TRANSLATIONS.en;
  // Only visitors who get the consent banner need a way back to it.
  const showCookieSettings = isConsentRegion();

  return (
    <footer className="w-full bg-night/50 backdrop-blur-md border-t border-white/10 pt-6 pb-24 md:pb-6 mt-8">
      <div className="max-w-7xl mx-auto px-4 flex flex-col items-center justify-center gap-2 text-sm text-muted font-medium">
        <p className="text-center text-xs text-muted">{newsTagline}</p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Link to={homeHref(lang)} className="py-3 md:py-2 hover:text-white transition-colors">{t.navTeamUps}</Link>
          <span className="text-white/20">|</span>
          <Link to={blogHref(lang)} className="py-3 md:py-2 hover:text-white transition-colors">{t.navBlog}</Link>
          <span className="text-white/20">|</span>
          <Link to={aboutHref(lang)} className="py-3 md:py-2 hover:text-white transition-colors">{t.navAbout}</Link>
          <span className="text-white/20 hidden sm:inline">|</span>
          <span className="w-full sm:w-auto text-center mt-2 sm:mt-0">© {new Date().getFullYear()} Rivals Base</span>
          <span className="text-white/20 hidden sm:inline">|</span>
          <span>{t.footerCreatedBy}</span>
          {showCookieSettings && (
            <>
              <span className="text-white/20 hidden sm:inline">|</span>
              <button type="button" onClick={openConsentSettings} className="py-3 md:py-2 hover:text-white transition-colors">{t.cookieSettingsLink}</button>
            </>
          )}
          <span className="text-white/20 hidden sm:inline">|</span>
          <div className="flex items-center md:gap-3" aria-label="Social media links">
            <a href={SOCIAL_LINKS.tiktok} onClick={() => trackSocialClick('tiktok', 'footer')} target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="p-[13px] md:p-2 rounded-full text-ink hover:text-white hover:bg-white/10 transition-colors">
              <TikTokIcon size={18} />
            </a>
            <a href={SOCIAL_LINKS.youtube} onClick={() => trackSocialClick('youtube', 'footer')} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="p-[13px] md:p-2 rounded-full text-ink hover:text-red-400 hover:bg-white/10 transition-colors">
              <YouTubeIcon size={18} />
            </a>
            <a href={SOCIAL_LINKS.instagram} onClick={() => trackSocialClick('instagram', 'footer')} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="p-[13px] md:p-2 rounded-full text-ink hover:text-pink-400 hover:bg-white/10 transition-colors">
              <InstagramIcon size={18} />
            </a>
            <a href={SOCIAL_LINKS.discord} onClick={() => trackSocialClick('discord', 'footer')} target="_blank" rel="noopener noreferrer" aria-label="Discord" className="p-[13px] md:p-2 rounded-full text-ink hover:text-indigo-400 hover:bg-white/10 transition-colors">
              <DiscordIcon size={18} />
            </a>
            <a href={SOCIAL_LINKS.twitch} onClick={() => trackSocialClick('twitch', 'footer')} target="_blank" rel="noopener noreferrer" aria-label="Twitch" className="p-[13px] md:p-2 rounded-full text-ink hover:text-purple-400 hover:bg-white/10 transition-colors">
              <TwitchIcon size={18} />
            </a>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
          <a
            href={BUY_ME_A_COFFEE_URL}
            onClick={() => trackSocialClick('buymeacoffee', 'footer')}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-yellow-400/50 bg-yellow-400/10 px-4 py-2.5 md:py-2 text-yellow-200 hover:bg-yellow-400/20 hover:text-yellow-100 transition-colors"
            aria-label={coffeeCta}
          >
            <span>{coffeeCta}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M10 2v2" />
              <path d="M14 2v2" />
              <path d="M16 8a1 1 0 0 1 1 1v8a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V9a1 1 0 0 1 1-1h14a4 4 0 1 1 0 8h-1" />
              <path d="M6 2v2" />
            </svg>
          </a>
          <PreferredSourceLink lang={lang} variant="compact" placement="footer" />
        </div>
      </div>
    </footer>
  );
}
